import { normalizeUrl } from './menuUtils';
import { LANDING } from '@/constants/product';
import { URLS } from '@/constants/urls';

// GENERAL HELPERS

export const isProduction = () => process.env.NODE_ENV === 'production';

export const isSSR = () => typeof window === 'undefined';

export const checkIE = () => typeof window !== 'undefined' && window.document ? !!window.document.documentMode : false;

export const sanitizeObject = (obj) => JSON.parse(JSON.stringify(obj));

const HTTP_URL_PATTERN = 'http';

// PRICES

export const round = (value, digits = 2) => {
  return Number((value).toFixed(digits));
};

export const getErrorMessageByKey = (t, key, defaultMessage = 'errors.genericErrorMessage.main') => {
  let message;

  if (key) {
    const translated = t(key);

    // t(key) return key if it don't find a correspondant value
    if (translated !== key) {
      message = translated;
    }
  }
  return message || t(defaultMessage);
};

export const getApiErrorMessage = (t, exception) => {
  let { errorCode } = exception.error || exception;
  let message;
  if (errorCode) {
    if (errorCode === '0') {
      errorCode = `genericErrorMessage.${exception.status}`;
    } else {
      errorCode = `errorCodeMessage.${errorCode}`;
    }
  } else if (exception.status) {
    errorCode = `genericErrorMessage.${exception.status}`;
  }
  const key = `errors.${errorCode}`;
  const translated = t(key);
  // t(key) return key if it don't find a correspondant value
  if (translated !== key) {
    message = translated;
  }
  return message || t('errors.genericErrorMessage.main');
};

export const getApiErrorTranslationKey = (exception) => {
  let messageKey = 'errors.genericErrorMessage.main';
  if (exception) {
    if (exception.error && exception.error.errorCode && exception.error.errorCode !== '' && exception.error.errorCode !== '0') {
      messageKey = `errors.errorCodeMessage.${exception.error.errorCode}`;
    } else if (exception.status && exception.status !== '') {
      messageKey = `errors.genericErrorMessage.${exception.status}`;
    }
  }
  return messageKey;
};

export const updateQty = (qty, partnumber, products) => {
  let product;
  if (qty !== 0 && products) {
    product = products.find((p) => getPartnumber(p) === partnumber);
  }
  return qty + (product ? product.quantity : 0);
};

export const getPartnumber = (element) => {
  let partnumber;
  if (element) {
    if (element.wineBox) {
      partnumber = element.id;
    } else if (element.product && element.product.item) {
      partnumber = element.custoText ? element.product.item.partnumber + element.custoText : element.product.item.partnumber;
    }
  }
  return partnumber;
};

export const getElementTopPositionFromBody = (element, marge = 0) => {
  const bodyRect = document.body.getBoundingClientRect();
  const elemRect = element.getBoundingClientRect();
  // Substract a little marge so improve positionning
  return elemRect.top - bodyRect.top - marge;
};

export const stringifyObject = (obj) => {
  if (obj !== null && typeof obj === 'object') {
    obj = JSON.stringify(obj);
  }
  return obj;
};

export const getAdyenScriptUrl = (adyenEnv = 'test') => {
  return URLS.ADYEN_SDK.replace('{0}', adyenEnv);
};

// STRINGS

/**
 * Replace all '/' by '.'
 * @param {string} value
 * @return {string}
 */
export const replaceForTranslationKey = (value) => (
  value.replace(/\//g, '.')
);

/**
 * Replace all ',' by '.' and remove '?+-'
 * @param {string} value
 * @return {string}
 */
export const formatForProgress = (value) => (
  value.replace(/,/g, '.').replace(/[?+-]/g, '')
);

/**
 * Remove all accent from the given string and convert it to lowercase
 * @param {string} value
 * @return {string}
 */
export const normalizeForCompare = (value) => (
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
);

/**
 * Parses an URL and returns the pathname prefixed with '@'
 * @param {string} url
 * @return {string|null} The parsed Twitter username
 */
export const getTwitterUsername = (url) => {
  if (!url) {
    return null;
  }

  const parsedUrl = new URL(removeEndSlash(url));

  return '@' + parsedUrl.pathname.slice(1);
};

export const isKrugOrWhispering = (seoKeyword) => !!seoKeyword && (
  seoKeyword.includes(LANDING.KRUG) || seoKeyword.includes(LANDING.WHISPERING)
);

export const sanitizeUrl = (host, keyword = '', isSpirit = false) => {
  let pathname = normalizeUrl(keyword); // Add a leading slash if not present
  if ((host.endsWith('/') || pathname.endsWith('/')) && !pathname.startsWith(HTTP_URL_PATTERN)) {
    pathname = pathname.slice(1); // Remove the leading slash
  }
  if (isSpirit) {
    let pathSpirits = 'spirits';
    if (!host.endsWith('/')) {
      pathSpirits = '/' + pathSpirits;
    }
    if (!pathname?.startsWith('/')) {
      pathSpirits = pathSpirits + '/';
    }
    pathname = pathSpirits + pathname;
  }
  if (pathname.includes(HTTP_URL_PATTERN)) {
    return pathname;
  }
  return host + pathname;
};

export const removeEndSlash = (url) => {
  return url.slice(0, url.endsWith('/') ? -1 : undefined);
};

export const langOrFullLocale = (fullLocale) => {
  const [locale, region] = fullLocale.toLowerCase().split('-');
  if (fullLocale === 'el-GR') {
    return 'el';
  }
  return region === locale ? locale : fullLocale;
};

// FEATURES

export const isFeatureEnabled = (storeConf, feature) => !!storeConf?.features?.[feature];

// CAROUSEL

export const carouselProductsToSlidesProps = ({ products, withDetails = true }) => products.map((product) => {
  if (product !== null) {
    const baseInfo = {
      _id: product._id,
      image: {
        src: product.image ? `${URLS.IMG.replace('{0}', 280)}/${product.image}` : `${URLS.NOIMG.replace('{0}', 280)}`,
        alt: product.productName
      },
      seoKeyword: normalizeUrl(product.seoKeyword),
      categ: product.attributes?.region?.value || product.attributes?.producteur?.value || '',
      product: product.productName,
      isPrivate: product.isPrivate,
      isExclusiveCellar: product.exclusive_cellar
    };

    if (!product.hasOwnProperty('items') || !withDetails) {
      return baseInfo;
    }

    const item = product?.items[0];

    const itemInfo = {
      price: item?.offerPrice,
      conditioning: `${item?.attributes.nbunit?.value}X${item?.attributes.conditionnement.shortValue}`,
      seoKeyWord: normalizeUrl(item?.seoKeyword),
      partnumber: item?.partnumber,
      promotion: item?.attributes.promotion
    };

    return { ...baseInfo, item: itemInfo };
  }
});
